/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect, useState } from 'react'
interface HelpPageProps {
  themeMode: string
}

const HelpPage: FC<HelpPageProps> = ({ themeMode }) => (
  <>
    <div className="card h-100">
      <div className="card-body">
        <div className="d-flex flex-column justify-content-center  gap-2 mb-6 h-100">
          <h1>VAMPP-score Help Guide</h1>

          <h3> Overview</h3>
          <p>VAMPP-score (<b>VA</b>riant <b>M</b>eta-<b>P</b>athogenicity <b>P</b>redictor-<b>s</b>core) is an advanced framework for missense variant interpretation, integrating multiple in silico pathogenicity predictors (ISPPs). It provides gene-specific ISPP accuracy scores, enabling researchers and clinicians to refine variant assessments with a statistically validated approach.</p>
          <h3>Key Features</h3>
          <ul>
            <li><b>VAMPP-score Calculation </b>: A statistical framework that integrates ISPPs with weighted scoring to assess missense variant pathogenicity. It also identifies the most suitable ISPPs for each gene, helping users select the most reliable predictors for variant interpretation.</li>
            <li><b>ISPP-Based Prediction Accuracy</b>: View gene-specific ISPP performance metrics and rankings to gain insights into predictor reliability and effectiveness.</li>
            <li><b>Gene & Variant Search</b>: Retrieve pathogenicity insights by searching for specific variants of genes within a comprehensive database.</li>
            <li><b>Interactive Visualizations</b>: Generate and download publication-ready figures illustrating variant distributions across ISPP scores.</li>
            <li><b>ACMG/AMP Criteria Integration</b>: Automated PP3/BP4 threshold adjustments refine computational predictions in line with established guidelines.</li>
            <li><b>Data Export & Reports</b>: Download variant prediction results, ISPP scores, and graphical representations for further analysis.</li>
          </ul>
          <h3>How It Works</h3>
          <h5>1. Search for a Variant or Gene</h5>
          <p>Users can search for a gene, such as <b>TP53</b>, to view a ranked list of ISPPs with their prediction accuracy, statistical comparisons, and categorization by scoring method to evaluate ISPP performance.</p>
          <img
            src={`/help-image1.jpg`}
            alt='help-image1'
            width={700}

          />
          <p>
            Searching for a specific variant provides its VAMPP-score (if available), along with ISPP scores, rankings, and downloadable figures showing ISPP score distributions across variant groups for the relevant gene, enabling a detailed evaluation of pathogenicity and ISPP reliability.
          </p>
          <img
            src={`/help-image2.jpg`}
            alt='help-image2'
            width={700}

          />
          <h5>2. Explore ISPP-Based Predictions</h5>
          <p>
            Identify the most reliable ISPPs for a specific gene based on statistical evaluation and compare pathogenicity likelihoods across different computational models.
          </p>
          <h5>3. Visualize & Download Figures</h5>
          <p>
            Access distribution charts for variant scores across gene-ISPP matches and download high-quality figures for presentations and publications.
          </p>
          <img
            src={`/help-image3.jpg`}
            alt='help-image3'
            width={700}

          />
          <h5 className='mt-2'>4. Interpret Results & Prioritize Variants</h5>
          <p >
            Use the VAMPP-score thresholds to re-classify variants by adjusting ACMG PP3/BP4 criteria.
          </p>
          <img
            src={`/help-image4.jpg`}
            alt='help-image4'
            width={600}

          />
          <p>
            Prioritize variants of uncertain significance (VUS) with confidence.
          </p>
          <h5 className='mt-2'>5. Export Data for Further Analysis</h5>
          <p>
            Download detailed variant tables and ISPP accuracy scores.
          </p>
          <img
            src={`/help-image5.jpg`}
            alt='help-image5'
            width={700}

          />
          <h3>Why Use VAMPP-score?</h3>
          <ul className='mt-2'>
            <li><b>More Accurate Predictions </b>– Uses gene-specific ISPP performance metrics rather than a one-size-fits-all approach.</li>
            <li><b>Data-Driven Prioritization </b>– Reduces false positives and false negatives by weighing ISPP scores effectively.</li>
            <li><b>Interactive and User-Friendly </b>– Designed for ease of use with an intuitive interface, requiring no coding experience.</li>
            <li><b>Designed for Clinical and Research Applications </b>– Supports rare disease diagnostics, variant reanalysis, and publication-ready outputs.</li>
          </ul>
          <h3>Accessing VAMPP-score</h3>
          <p>
            VAMPP-score is available via a web platform vamppscore.com. For further details such as datasets and source codes, please visit the <a href='https://github.com/eylulaydin7/VAMPP-score' target='_blank' rel='noreferrer'>GitHub page.</a><br /><br />
            Users can download data for a selected gene or variant from the respective page or retrieve the full dataset from the Downloads section.
          </p>
        </div>

      </div>
    </div>
  </>
)

const HelpPageWrapper: FC = () => {
  const [themeMode, setThemeMode] = useState<string>('light')

  useEffect(() => {
    const currentTheme = document.documentElement.getAttribute('data-bs-theme')
    setThemeMode(currentTheme === 'light' ? 'light' : 'dark')

    const observer = new MutationObserver(() => {
      const updatedTheme = document.documentElement.getAttribute('data-bs-theme')
      setThemeMode(updatedTheme === 'light' ? 'light' : 'dark')
    })

    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-bs-theme'] })

    return () => observer.disconnect()
  }, [])
  const sampleData = [
    { category: "A", value: 10 },
    { category: "A", value: 15 },
    { category: "B", value: 20 },
    { category: "B", value: 25 },
    { category: "C", value: 30 },
    { category: "C", value: 35 },
  ];
  return (
    <>
      <HelpPage themeMode={themeMode} />
    </>
  )
}

export { HelpPageWrapper }
